/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { MutableRef } from 'preact/hooks';
import type { ColumnResizeDetail, ColumnResizingDetail } from '#UNSAFE_TableView';
import type { AppliedColumnWidths, ColumnResizeInfo, TableColumn } from '../Table';

import { getElementContainsFunc } from '#utils/PRIVATE_collectionUtils';
import { getHeaderRowElement, TABLE_HEADER_SELECTOR } from './TableDomUtils';

/**
 * Helper function to handle a hover over a resizable column region gesture.
 */
export function handleOverResizeRegionGesture<K, D, C>(
  rootElement: HTMLDivElement,
  tableId: string,
  event: PointerEvent,
  columnsArray: TableColumn<K, D, C>[],
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean
) {
  // handle hover feedback around potentially resizable columns
  _updateResizingState(rootElement, tableId, event, columnsArray, resizeInfoRef, isRtl);
}

/**
 * Helper function to handle a column resize start gesture (pointer down).
 */
export function handleColumnResizeStartGesture<K, D, C extends string>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  tableId: string,
  event: PointerEvent,
  columnsArray: TableColumn<K, D, C>[],
  appliedColumnWidths: AppliedColumnWidths<C>,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean,
  hideTooltip: (isImmediate?: boolean) => void,
  onColumnResizing?: (detail: ColumnResizingDetail<C>) => void
) {
  if (resizeInfoRef.current.key == null) {
    _updateResizingState(rootElement, tableId, event, columnsArray, resizeInfoRef, isRtl);
  }
  if (resizeInfoRef.current.key != null) {
    const rect = scrollerElement.getBoundingClientRect();
    const pointerX = isRtl ? rect.right - event.clientX : event.clientX - rect.left;

    resizeInfoRef.current = {
      isResizing: true,
      isResizeHover: true,
      key: resizeInfoRef.current.key,
      startX: pointerX,
      delta: 0,
      ignoreClick: resizeInfoRef.current.ignoreClick
    };
    if (onColumnResizing != null) {
      onColumnResizing({
        key: resizeInfoRef.current.key,
        delta: 0,
        allColumnWidths: appliedColumnWidths.netColumnWidths,
        paddingWidth: appliedColumnWidths.totalBorderOffset
      });
    }
    hideTooltip(true);
    return true;
  }
  return false;
}

/**
 * Helper function to ensure the resizing state is updated based on the latest pointer event.
 */
function _updateResizingState<K, D, C>(
  rootElement: HTMLDivElement,
  tableId: string,
  event: PointerEvent,
  columnsArray: TableColumn<K, D, C>[],
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean
) {
  const headerRowElement = getHeaderRowElement(rootElement, tableId);
  if (headerRowElement != null) {
    const headerElements = headerRowElement.querySelectorAll(
      `[${TABLE_HEADER_SELECTOR}='${tableId}']`
    ) as NodeListOf<HTMLElement>;
    if (getElementContainsFunc(headerRowElement)(event.target as HTMLElement)) {
      let isResizeHover = false;
      for (let i = 0; i < headerElements.length; i++) {
        const headerElement = headerElements[i];
        const cellRect = headerElement.getBoundingClientRect();
        const endEdge = isRtl ? cellRect.left : cellRect.right;
        if (Math.abs(endEdge - event.pageX) <= 8) {
          if (columnsArray[i].value.edgeResizable !== 'enabled') {
            break;
          }
          // resize operation on end side of header cell
          resizeInfoRef.current.key = columnsArray[i].key;
          headerRowElement.style.cursor = 'col-resize';
          isResizeHover = true;
          resizeInfoRef.current.isResizeHover = true;
        }
      }
      if (!isResizeHover) {
        headerRowElement.style.cursor = '';
        resizeInfoRef.current.key = undefined;
        resizeInfoRef.current.isResizeHover = false;
      }
    } else {
      if (headerRowElement != null) {
        headerRowElement.style.cursor = '';
      }
      resizeInfoRef.current.key = undefined;
      resizeInfoRef.current.isResizeHover = false;
    }
  }
}

/**
 * Helper function to handle a pointer move gesture during a column resize.
 */
export function handleColumnResizingGesture<C extends string>(
  scrollerElement: HTMLElement,
  event: PointerEvent,
  appliedColumnWidths: AppliedColumnWidths<C>,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean,
  onColumnResizing?: (detail: ColumnResizingDetail<C>) => void
) {
  if (resizeInfoRef.current.isResizing) {
    const rect = scrollerElement.getBoundingClientRect();
    const currentX = isRtl ? rect.right - event.clientX : event.clientX - rect.left;

    resizeInfoRef.current.delta = currentX - resizeInfoRef.current.startX;
    // handle resize move operation
    if (onColumnResizing != null) {
      onColumnResizing({
        key: resizeInfoRef.current.key,
        delta: resizeInfoRef.current.delta,
        allColumnWidths: appliedColumnWidths.netColumnWidths,
        paddingWidth: appliedColumnWidths.totalBorderOffset
      });
    }
  }
}

/**
 * Helper function to handle a column resize end gesture (pointer up).
 */
export function handleColumnResizeEndGesture<C extends string>(
  scrollerElement: HTMLElement,
  event: PointerEvent,
  appliedColumnWidths: AppliedColumnWidths<C>,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean,
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void
) {
  if (resizeInfoRef.current.isResizing) {
    if (onColumnResize != null) {
      const rect = scrollerElement.getBoundingClientRect();
      const currentX = isRtl ? rect.right - event.clientX : event.clientX - rect.left;

      resizeInfoRef.current.delta = currentX - resizeInfoRef.current.startX;
      onColumnResize({
        key: resizeInfoRef.current.key,
        delta: resizeInfoRef.current.delta,
        allColumnWidths: appliedColumnWidths.netColumnWidths,
        paddingWidth: appliedColumnWidths.totalBorderOffset,
        gesture: 'pointer',
        type: 'delta'
      });
    }
  }
  resizeInfoRef.current = {
    isResizing: false,
    isResizeHover: resizeInfoRef.current.isResizeHover,
    ignoreClick: resizeInfoRef.current.ignoreClick
  };
}

/**
 * Helper function to handle the pointer leaving the entire Table when previously hovering
 * over a column resize region.
 */
export function handleLeaveResizingGesture<C>(
  rootElement: HTMLDivElement,
  tableId: string,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>
) {
  if (!resizeInfoRef.current.isResizing) {
    const headerRowElement = getHeaderRowElement(rootElement, tableId);
    if (headerRowElement != null) {
      headerRowElement.style.cursor = '';
    }
    resizeInfoRef.current.isResizeHover = false;
  }
}

/**
 * Helper function to handle a pointer entering the Table when it was previously handling
 * a column resize gesture.
 */
export function handleEnterResizingGesture<C extends string>(
  scrollerElement: HTMLElement,
  event: PointerEvent,
  appliedColumnWidths: AppliedColumnWidths<C>,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean,
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void
) {
  // handle case where user was resizing, moved pointer out of the Table,
  // released the button, and has now entered the table once again
  if (resizeInfoRef.current.isResizing && event.buttons === 0) {
    handleColumnResizeEndGesture(
      scrollerElement,
      event,
      appliedColumnWidths,
      resizeInfoRef,
      isRtl,
      onColumnResize
    );
  }
}
